import { PartyStats } from "./CaseModel.ts";

/**
 * How much is at stake in a case, the size the settlement scatters place their
 * dots by. Kept apart from the page so the arithmetic can be unit-tested.
 */

/**
 * What a party has at stake in a case: the larger of what it claims and what
 * is claimed against it.
 */
export const claimValueOf = ({ stats }: PartyStats): number =>
  Math.max(
    stats.quantumsClaimed.totalValue,
    stats.quantumsClaimedAgainst.totalValue,
  );

/**
 * What is at stake in each case: the largest claim value among its parties in
 * `stats`. The stats are kept by party, so a reader that knows only the case
 * would have to ask every party for it; this turns them round once.
 */
export function claimValueByCase(stats: {
  [partyId: string]: { [caseId: string]: PartyStats };
}): Map<string, number> {
  const byCase = new Map<string, number>();
  for (const ofParty of Object.values(stats))
    for (const [caseId, partyStats] of Object.entries(ofParty))
      byCase.set(
        caseId,
        Math.max(byCase.get(caseId) ?? 0, claimValueOf(partyStats)),
      );
  return byCase;
}

/**
 * Each row with its claim value as a share of the largest among them, 0…1,
 * which is where a scatter places it from bottom to top. A row with nothing at
 * stake sits at the bottom.
 */
export function withClaimNorm<R extends { claimValue: number }>(
  rows: readonly R[],
): (R & { claimNorm: number })[] {
  const largest = Math.max(1, ...rows.map(({ claimValue }) => claimValue));
  return rows.map((row) => ({
    ...row,
    claimNorm: row.claimValue > 0 ? row.claimValue / largest : 0,
  }));
}
